import { themeNames } from "@msteams/bridges-models-theme";
import {
  defaultPalette,
  darkPalette,
  contrastPalette
} from "@msteams/bridges-models-common";
import { IFileNavigationParams } from "@msteams/bridges-models-ts-navigation";
import { isEmpty, toLower } from "lodash";
import {
  filesCtx,
  serviceNames,
  FileIcons,
  FilesProviderTypeMap,
  FilesProviderType,
  lcids,
  ISiteInfo,
  IPostMessageScenarioMarker,
  IPostMessageScenarioStopFailure,
  IPostMessageScenarioStopSuccess,
  IPostMessage
} from "@msteams/bridges-models-files";
import {
  isKnownExtension,
  unknownExtension
} from "@msteams/services-utilities-files-extensions";
import { ChildWindowEventRequests } from "@msteams/core-services-common";
import { getWindow } from "@msteams/services-utilities-browser-window";
import { ScenarioName } from "@msteams/core-services-telemetry";

/*@function isInCloudStorage
 * @param {siteInfo} ISiteInfo object of the site, for which the check is being done
 * @param {serviceName} OPTIONAL - type of site i.e. teams, personal etc
 * @return {Boolean} whether cloudStorage is available or not
 */
export function isInCloudStorage(siteInfo: ISiteInfo, serviceName?: string) {
  /**
   * 1. Should be in channel folders
   * 2. rootFolderPath should be defined. Undefined implies user is on root folder hence, cannot be cloud storage.
   * 3. rootChannelPath should be defined for Channel Files.
   * 4. rootFolderPath should not contain rootChannelPath in case user is currently inside Cloud Storage folder.
   */
  if (
    serviceName === serviceNames.teams &&
    siteInfo.rootFolderPath &&
    siteInfo.rootChannelPath &&
    siteInfo.rootFolderPath.indexOf(siteInfo.rootChannelPath) === -1
  ) {
    return true;
  }
  return false;
}

/*@function getPalette
 * @param {themeNames} theme names for the different themes supported in teams
 * @return {Partial<IPalette>} Fabric palette for the given theme
 */
export function getPalette(theme: themeNames) {
  switch (theme) {
    case themeNames.default:
      return defaultPalette;
    case themeNames.dark:
      return darkPalette;
    case themeNames.contrast:
      return contrastPalette;
  }
}

/**
 * Returns serviceName based on supplied parameters. Used in navigation service
 * @param spItemKey
 * @param threadId
 * @param googleDriveItemKey
 */
export function getServiceName(
  webAbsoluteUrl: string,
  threadId: string,
  googleDriveItemId?: string,
  wopiItemId?: string
): string {
  let serviceName = "";
  if (webAbsoluteUrl) {
    serviceName = threadId ? serviceNames.teams : serviceNames.personal;
  } else if (googleDriveItemId) {
    serviceName = serviceNames.google;
  } else if (wopiItemId) {
    serviceName = serviceNames.wopi;
  }
  return serviceName;
}

/**
 * gets file navigation parameters for google files
 * @param fileExtension file extension
 * @param googleDriveItemKey googledrive itemkey
 */
export function getGoogleFileNavigationParams(
  fileExtension: string,
  googleDriveItemId: string
): IFileNavigationParams {
  return {
    fileId: googleDriveItemId,
    providerCode: "GOOGLEDRIVE",
    providerType: String(FilesProviderType.Google),
    fileType: fileExtension,
    ctx: filesCtx.externalCtx
  };
}

/**
 * gets file navigation parameters for google files
 * @param fileExtension file extension
 * @param wopiItemId wopi itemkey
 * @param providerCode wopi provider code
 */
export function getWopiFileNavigationParams(
  fileExtension: string,
  wopiItemId: string,
  providerCode: string
): IFileNavigationParams {
  return {
    fileId: wopiItemId,
    providerType: String(FilesProviderType.WOPI),
    providerCode,
    fileType: fileExtension,
    ctx: filesCtx.externalCtx
  };
}

/**
 * gets file navigation parameters for odsp files
 * @param oneNote is file a onenote
 * @param rowData SP rowdata
 * @param webAbsoluteUrl web absolute url
 * @param listFullUrl list full url
 * @param serviceName serviceName (teams/personal/google)
 * @param threadId conversation thread id
 * @param isNewFile if new file creation
 */
export function getODSPFileNavigationParams(
  webAbsoluteUrl: string,
  listFullUrl: string,
  fileExtension: string,
  serviceName: string,
  uniqueId: string,
  folderPath: string | undefined,
  threadId: string | undefined,
  isNewFile: boolean
): IFileNavigationParams {
  const protocolLength = 8;
  return {
    baseUrl: webAbsoluteUrl,
    fileId: uniqueId,
    fileType: fileExtension,
    objectUrl: folderPath
      ? `${listFullUrl.substring(
          0,
          listFullUrl.indexOf("/", protocolLength)
        )}${folderPath}`
      : undefined,
    serviceName,
    threadId,
    ctx: isNewFile
      ? filesCtx.newFileCtx
      : threadId
      ? filesCtx.channelFileCtx
      : undefined
  };
}
/**
 * @param  {string} path
 * Removes the context path from the last forward slash to the end.
 */
export function removeFileNameFromObjectUrl(path: string): string {
  if (!path) {
    return path;
  }

  return path.substring(0, path.lastIndexOf("/"));
}

/**
 * Retrieves fileType(word) from fileExtension(docx, doc, docm etc)
 * @param fileExtension
 */
// eslint-disable-next-line complexity
export function getFileTypeClass(fileExtension: string): FileIcons {
  let className = FileIcons.Generic;
  switch (fileExtension && fileExtension.toLowerCase()) {
    case "docx":
    case "doc":
    case "docm":
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.template":
    case "application/vnd.ms-word.document.macroEnabled.12":
    case "application/vnd.ms-word.template.macroEnabled.12":
      className = FileIcons.Word;
      break;

    case "xlsx":
    case "xlsb":
    case "xls":
    case "xlsm":
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
    case "application/vnd.ms-excel.sheet.macroEnabled.12":
    case "application/vnd.ms-excel.template.macroEnabled.12":
    case "application/vnd.ms-excel.addin.macroEnabled.12":
    case "application/vnd.ms-excel.sheet.binary.macroEnabled.12":
      className = FileIcons.Excel;
      break;

    case "pptx":
    case "potm":
    case "ppt":
    case "pptm":
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.openxmlformats-officedocument.presentationml.template":
    case "application/vnd.openxmlformats-officedocument.presentationml.slideshow":
    case "application/vnd.ms-powerpoint.addin.macroEnabled.12":
    case "application/vnd.ms-powerpoint.presentation.macroEnabled.12":
    case "application/vnd.ms-powerpoint.template.macroEnabled.12":
    case "application/vnd.ms-powerpoint.slideshow.macroEnabled.12":
      className = FileIcons.Powerpoint;
      break;

    case "vsdx":
    case "vsdm":
    case "vsd":
      className = FileIcons.Visio;
      break;

    case "one":
    case "onepkg":
    case "notebk":
      className = FileIcons.Onenote;
      break;
  }
  return className;
}

/**
 * Extract domain from site URL
 * @param siteUrl URL of the site
 */
export function getDomainFromSiteUrl(siteUrl: string): string {
  const domainPartLength = 3;
  const parts = getPartsFromSiteURL(siteUrl);
  if (isEmpty(parts)) {
    return "";
  }
  return parts.slice(0, domainPartLength).join("/");
}

/**
 * Extract serverRelativeURL from siteURL
 * @param siteUrl URL of the site
 */
export function getServerRelativeUrlFromSiteUrl(siteUrl: string): string {
  const domainPartLength = 3;
  const parts = getPartsFromSiteURL(siteUrl);
  if (isEmpty(parts)) {
    return "";
  }
  return `/${parts.slice(domainPartLength).join("/")}`;
}

/**
 * Extract siteUrl from objectURL
 * @param ObjectURL URL of the site
 */
export function getSiteUrl(objectURL: string): string {
  const domainPartLength = 5;
  const parts = getPartsFromSiteURL(objectURL);
  if (isEmpty(parts)) {
    return "";
  }
  return parts.slice(0, domainPartLength).join("/");
}

function getPartsFromSiteURL(siteUrl: string): string[] {
  if (!siteUrl || siteUrl.length === 0) {
    return [];
  }
  const domainPartLength = 3;
  const serverRelativeSiteUrlSegments = siteUrl.split("/");
  if (
    !serverRelativeSiteUrlSegments ||
    serverRelativeSiteUrlSegments.length < domainPartLength
  ) {
    return [];
  }
  return serverRelativeSiteUrlSegments;
}

/**
 * This function lookup for file type in a predefined extensions list,
 * & if the file type present in this list, it returns file type as it is
 * otherwise returns "other".
 * @param fileType file-extension type like docx, excel, js, etc..
 */
export function scrubUnknownFileExtensions(fileType: string): string {
  if (!fileType) {
    return fileType;
  }
  if (isKnownExtension(fileType.toLowerCase())) {
    return fileType;
  }
  return unknownExtension;
}

/**
 * Gets file extension
 * @param filename Name of the file including extension
 * @returns file extension
 */
export function getFileExtension(filename: string): string {
  const re = new RegExp("(?:.([^.]+))?$");
  const matchedObjects = re.exec(filename);
  return toLower(matchedObjects && matchedObjects[1] ? matchedObjects[1] : "");
}

/**
 * Drops object url query params
 * @param objectUrl
 * @returns URL without any query parameters. E.g teams.ms.com/files?a=b will result in teams.ms.com/files
 */
export function dropObjectUrlQueryParams(objectUrl: string): string {
  return (objectUrl || "").split("?")[0];
}
/**
 * convert the given string value to a FilesProviderType enum
 * @param dtoType a string with value as sharepoint, wopi or google
 */
export function convertStringToProviderType(
  dtoType: string
): FilesProviderType {
  return FilesProviderTypeMap[
    dtoType.toLowerCase() as keyof typeof FilesProviderType
  ];
}
/**
 * @param  {string} localeCode
 * @returns Returns LCID (Language code identifier) for the required translation code
 */
export function getLCID(localeCode: string) {
  const [market, country] = localeCode.split("-");
  const locale = `${market.toLowerCase()}_${country.toUpperCase()}`;
  return lcids[locale] || lcids["unknown"];
}
/**
 * convert the given string value which was serialized from the original enum value into a string back into the original enum
 * @param serializedProviderType a string which obtained by serializing the original enum value as a string
 */
export function convertSerializedStringToProviderType(
  serializedProviderType: string
): FilesProviderType {
  // Convert string to original number value
  const providerCodeEnumValue = Number(serializedProviderType);
  // Use number value to get a valid enum key
  const providerCodeEnumKey = FilesProviderType[
    providerCodeEnumValue
  ] as keyof typeof FilesProviderType;
  // Return correct provider type using the obtained key
  return FilesProviderType[providerCodeEnumKey];
}

/**
 * For CFT, this will check whether the current folder is at the root or not
 * @param siteInfo site info for the current channel
 */
export function isRootChannelFolder(siteInfo: ISiteInfo | undefined): boolean {
  if (!siteInfo || !siteInfo.rootChannelPath) {
    return false;
  }
  return siteInfo.rootChannelPath === siteInfo.rootFolderPath;
}

/**
 * Add sendTime in post-message payload to measure IpcTimeOverhead
 */
export function postMessage(
  event: ChildWindowEventRequests,
  payload?:
    | IPostMessage
    | IPostMessageScenarioMarker
    | IPostMessageScenarioStopSuccess
    | IPostMessageScenarioStopFailure
) {
  getWindow().opener.postMessage(event, {
    sendTime: Date.now(),
    ...payload
  });
}

/**
 * This will extract strings like "correlationId":"123-456-321" from the blob message passed as the param,
 * basically it will match the strings of the below format:
 * <anything_before>"correlationId":"value"<anything_after>
 */
export function extractCorrelationId(msg: string | undefined): string {
  if (!msg) {
    return "";
  }
  const correlationRegex = new RegExp(`"correlationId":"[^"]+`);
  const result = msg.match(correlationRegex);
  return result ? result[0] : "";
}

/**
 * Extract origin from string. Return empty string, in case of invalid input.
 * @param url URL string
 */
export function getOriginFromUrl(url: string | undefined): string {
  if (!url) {
    return "";
  }
  const reURLInformation = new RegExp(
    [
      "^(https?:)//", // protocol
      "(([^:/?#]*)(?::([0-9]+))?)" // host (hostname and port)
    ].join(""),
    "i"
  );
  const matches = url.match(reURLInformation);
  return (matches && matches[0]) || "";
}

/**
 * Get scenarioName given fileType in Files Experince
 * @param fileType type of file
 */
export function getScenarioName(fileType: string): ScenarioName {
  switch (fileType) {
    case "eml":
      return ScenarioName.RenderFileEmailViewer;
    default:
      return ScenarioName.RenderFileViewer;
  }
}
